import * as React from "react";
import { connect } from "react-redux";
import { match } from "react-router";
import { mapDispatchToProps } from "../../../redux/actions";
import { updateUnitDesign } from "../../../api-utilities/performanceUpdate";
import { callGetEndpoint, updateInputField } from "../../../api-calls/readApiService";
import {
  getValidationError,
  getSelectBoxOptions,
  getInputCSS,
  getFilteredValidationErrors,
  getNewDropdownList,
  getNewValidationErrorsList
} from "../../../utilities/pageUtils";
import {addNotification} from "../../../api-utilities/notification-tools";
import { formatNumber } from "../../../api-utilities/formatting";
import "../../../css/Accordions.scss";

// expected props
interface IProps {
  match: match<any>;
  updateRedux: any;
  targetVertex: string;
  reduxMenus: any;
}
// local state
interface IState {
  userInputs: any;
  displayFields: any;
  dropdownOptions: any[];
  validationErrors: any[];
  lastUpdatedTextbox: string;
  operatingMode: any;
  filterSizeUsed: string;
}
class Filter extends React.Component<IProps, IState> {
  public state: IState = {
    userInputs: null,
    displayFields: null,
    dropdownOptions: [],
    validationErrors: [],
    lastUpdatedTextbox: "",
    operatingMode: null,
    filterSizeUsed: ""
  };

  async componentDidMount() {
    await this.getFilterData(this.props.targetVertex);
  }

  async componentDidUpdate(prevProps: IProps) {
    if (prevProps.targetVertex != this.props.targetVertex) {
      await this.getFilterData(this.props.targetVertex);
    }
  }

  private async getFilterData(targetVertex: string) {
    const { projectId, unitId } = this.props.match.params;
    const result = await callGetEndpoint(`readapi/GetFilterInputValues/${targetVertex}`, projectId, unitId);

    if (result.hasError) {
      const notification = {
        id: "filterInputsError",
        notificationType: "error",
        title: "Error",
        content: <div className="nf-div">GetFilterInputValues {result.errorMessage}</div>
      };
      addNotification(notification);
    }

    if (result.uiDataContainer && result.uiDataContainer.filterInputs) {
      this.setState({
        userInputs: result.uiDataContainer.filterInputs,
        displayFields: result.uiDataContainer.displayFields,
        dropdownOptions: result.dropdownOptions,
        validationErrors: result.validationErrors,
        operatingMode: result.uiDataContainer.filterOpMode,
        filterSizeUsed: result.uiDataContainer.filterSizeUsed
      });
    }
  }
  private async RunPerformance(targetVertex: string){
    const { projectId, unitId } = this.props.match.params;
   
    this.props.updateRedux("UPDATE_DISPLAY", { showLoader: true });
    //Run performance to update read-only props for filters and update optimized design numbers.
    let perResponse = await updateUnitDesign(projectId, unitId);
    const result = await callGetEndpoint(`readapi/GetFilterInputValues/${targetVertex}`, projectId, unitId);

    if (result.hasError) {
      const notification = {
        id: "filterInputsError",
        notificationType: "error",
        title: "Error",
        content: <div className="nf-div">GetFilterInputValues {result.errorMessage}</div>
      };
      addNotification(notification);
    }

    if (result.uiDataContainer && result.uiDataContainer.filterInputs) {
      this.setState({
        userInputs: result.uiDataContainer.filterInputs,
        displayFields: result.uiDataContainer.displayFields,
        dropdownOptions: result.dropdownOptions,
        validationErrors: result.validationErrors,
        operatingMode: result.uiDataContainer.filterOpMode,
        filterSizeUsed: result.uiDataContainer.filterSizeUsed
      });
    }
    this.props.updateRedux("UPDATE_DISPLAY", { showLoader: false });
    this.props.updateRedux("UPDATE_MENUS", { optimizedDesignInputChanged: false });
  }
  //This function gets called on every keystroke in a textbox.
  public async updateTextboxValue(fieldName: string, newValue: string) {
    const newState = Object.assign({}, this.state.userInputs, { [fieldName]: newValue });
    this.setState({ userInputs: newState, lastUpdatedTextbox: fieldName });
  }

  //This gets called by textbox onBlur events
  public async saveTextboxValue(fieldName: string, newValue: string) {
    //If field value hasn't changed, don't call the server
    if (this.state.lastUpdatedTextbox !== fieldName) {
      return;
    }
    this.saveInputValue(fieldName, newValue);
  }

  //Save new value to redis and DB
  public async saveInputValue(fieldName: string, newValue: string) {
    const { projectId, unitId } = this.props.match.params;
    //const filterType = this.props.filterType;
    this.removeValidationError(fieldName);

    let result = await updateInputField("updateFilter", projectId, unitId, fieldName, newValue, this.props.targetVertex);

    if (result.success) {
      this.props.updateRedux("UPDATE_MENUS", { optimizedDesignInputChanged: true });
      //If there are any errors in the response, add them to state
      if (result.data.validationErrors != null) {
        this.addValidationErrors(result.data.validationErrors);
      }

      //If there are any dropdownLists present in the response, then update those in state
      if (result.data.dropdownOptions != null) {
        this.updateDropdowns(result.data.dropdownOptions);
      }

      //If uiDataContainer was updated with new values, then update it in state
      if (result.data.uiDataContainer != null) {
        this.setState({
          userInputs: result.data.uiDataContainer.filterInputs,
          displayFields: result.data.uiDataContainer.displayFields,
          filterSizeUsed: result.data.uiDataContainer.filterSizeUsed
        });
      }
    }
  }

  //Check for errors on this textbox/selectbox, and return the appropriate CSS className
  public getCSS(fieldName: string, type: string) {
    return getInputCSS(this.state.validationErrors, fieldName, type);
  }

  //Update dropdowns with new options from the server
  public updateDropdowns(newDropdownOptions: any) {
    let newList = getNewDropdownList(this.state.dropdownOptions, newDropdownOptions);
    this.setState({ dropdownOptions: newList });
  }

  //Add any validation errors we got from the server to state
  public addValidationErrors(validationErrors: any[]) {
    let newList = getNewValidationErrorsList(this.state.validationErrors, validationErrors);
    this.setState({ validationErrors: newList });
  }

  public getValidationError(fieldName: string) {
    //Call the validation method from pageUtils
    return getValidationError(this.state.validationErrors, fieldName, "validation-err-right");
  }

  //Whenever we update a field, remove any previous error for that field
  //Also reset the lastUpdatedTextbox in state
  public removeValidationError(fieldName: string) {
    let newArray = getFilteredValidationErrors(this.state.validationErrors, fieldName);
    this.setState({ validationErrors: newArray, lastUpdatedTextbox: "" });
  }
  //------------------------------------------------------------------------------------------

  public render() {
    const savedValues = this.state.userInputs;
    const displayFields = this.state.displayFields;
    let operatingMode = this.state.operatingMode;
    let filterSizeUsed = this.state.filterSizeUsed;

    if (!savedValues) {
      return null;
    }

    let label = "";
    if (displayFields) {
      label = displayFields.isSingleTunnel ? "Supply" : "Outside";
    }

    let title =
      this.props.targetVertex == "SupplyAirFinalFilterInputs"
        ? "Supply Air Final Filter"
        : this.props.targetVertex == "ReturnAirFilterInputs"
        ? "Return Air Filter"
        : "";

    return (
      <fieldset className="optimize-fieldset">
        <legend className="optimize-legend">{title}</legend>

        <div className="accordion-content">
          <div className="acc-fan-spacer"></div>
          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">Loading Level</div>
              <div className="accordion-input">
                <select
                  className={this.getCSS("loadingLevel", "accordionselect")}
                  value={savedValues.loadingLevel || ""}
                  onChange={(event) => this.saveInputValue("loadingLevel", event.currentTarget.value)}
                >
                  {getSelectBoxOptions(this.state.dropdownOptions, "loadingLevelOptions")}
                </select>
              </div>
              {this.getValidationError("loadingLevel")}
            </div>
            <div className="accordion-label-90-left">Type</div>
            <div className="acc-label-ellip acc-label-110">{savedValues.filterType}</div>
          </div>

          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">Filter Removal</div>
              <div className="accordion-input">
                <select
                  className={this.getCSS("filterRemoval", "accordionselect")}
                  value={savedValues.filterRemoval || ""}
                  onChange={(event) => this.saveInputValue("filterRemoval", event.currentTarget.value)}
                  disabled={displayFields.filterRemovalDisabled}
                >
                  {getSelectBoxOptions(this.state.dropdownOptions, "filterRemovalOptions")}
                </select>
              </div>
              {this.getValidationError("filterRemoval")}
            </div>
            <div className="accordion-label">Face Velocity</div>
            <div className="accordion-label-numeric">{formatNumber(operatingMode.faceVelocity, 0)}</div>
          </div>

          <div className="accordion-row">
            <div className="accordion-label"></div>
            <div className="accordion-input"></div>
            <div className="accordion-label">Pressure Drop</div>
            <div className="accordion-label-numeric">{formatNumber(operatingMode.airPressureDrop, 2)}</div>
          </div>
          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">Filter Size(s) Allowed</div>
              <div className="accordion-input">
                <select
                    className={this.getCSS("filterSizeRestriction", "accordionselect")}
                    value={savedValues.filterSizeRestriction || ""}
                    onChange={(event) => this.saveInputValue("filterSizeRestriction", event.currentTarget.value)}
                  >
                    {getSelectBoxOptions(this.state.dropdownOptions, "filterSizeOptions")}
                </select>
              </div>
              {this.getValidationError("filterSizeRestriction")}
            </div>
            <div className="accordion-label">Filter Size(s) Used</div>
            <div className="accordion-label">{filterSizeUsed}</div>
          </div>

          <div className={this.props.reduxMenus.optimizedDesignInputChanged ? "perf-reset-btn-small drawing-update-design-margin" : "perf-reset-btn-small-disabled drawing-update-design-margin"}>
            <div onClick={() => this.RunPerformance(this.props.targetVertex)}>Update Design</div>
          </div>
          <div className="accordion-row"></div>
        </div>
      </fieldset>
    );
  }
}

//------------------ Redux ----------------------------
function mapStateToProps(state: any) {
  return {
    reduxMenus: state.reduxMenus
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(Filter);
